/**
 * WHO THIS GRANT WOULD BELONG TO.
 *
 * A grant has an OWNER, and it is not always you. `/authorize/approve` sets the
 * EP's owner to the group's own account when `group_id` is sent, so authorizing
 * for a group gives the app nothing of your personal graph and vice versa —
 * two independent grants, not one that widened.
 *
 * The page has always been able to do this. `GrantRoute` reads `group_id` from
 * the URL and the server answers with `acting_as`, so the group case renders
 * correctly today. What it could not do is OFFER the choice: the only way to
 * reach a group grant was to arrive with the id already in the query string,
 * which in practice meant entering the app, finding the seat and pressing
 * Connect — four moves past the page that mints the thing.
 *
 * ADMINS ONLY, AND NOT AS A COURTESY. `authorization.py` gates both the preview
 * and the approve on `_is_group_admin` and 403s a member with "Only group admins
 * can authorize apps on behalf of a group". A group you merely belong to is
 * therefore absent here rather than present-and-disabled: the backend already
 * withholds group facts from non-admins (it hides the group's spending from them
 * for the same reason), and a row that can never be pressed is noise on the one
 * decision every other slot depends on.
 *
 * Pure, so the rule can be tested without a page: the component below it only
 * draws what these functions return.
 */

/** The role `GET /groups` reports for the caller on each group (`my_role`). */
export const ADMIN = 'admin';

/**
 * The actors this person may mint a grant for, you first.
 *
 * @param {object} args
 * @param {string} [args.username]  the signed-in person's name.
 * @param {Array}  [args.groups]    `GET /groups`, each carrying `my_role`.
 * @returns {Array<{id: string|null, name: string, kind: 'you'|'group', role: string|null}>}
 *          `id` is what goes on the wire as `group_id`; **null is you**, which is
 *          why it is null rather than absent — the caller switches on it.
 */
export function actorsFor({ username, groups } = {}) {
  const you = { id: null, name: username || 'You', kind: 'you', role: null };
  const admin = (groups || [])
    .filter((g) => g && g.id && g.my_role === ADMIN)
    .map((g) => ({ id: String(g.id), name: g.name || 'Group', kind: 'group', role: g.my_role }));
  return [you, ...admin];
}

/**
 * The actor a given `group_id` names, or you when it names none.
 *
 * Falls back to you for an id that is not in the list — a group whose admin
 * rights were removed between page load and now would otherwise select nothing
 * and leave the line blank about the most important fact on the page.
 */
export function selectedActor(actors = [], groupId) {
  if (!groupId) return actors[0] || null;
  return actors.find((a) => a.id === String(groupId)) || actors[0] || null;
}

/** Everyone except the one in force. What the "grant for … instead" link offers. */
export function alternatives(actors = [], groupId) {
  const on = selectedActor(actors, groupId);
  return actors.filter((a) => a !== on);
}

/**
 * Is there a choice to put on screen at all?
 *
 * One actor means no groups you administer, and then this whole component must
 * not render: the page is then exactly what it is today, which is the promise
 * that makes this safe to add to every app edge rather than a new one.
 */
export function hasChoice(actors = []) {
  return actors.length > 1;
}
